import { X } from '@phosphor-icons/react';
import { ClipboardEvent, FocusEvent, KeyboardEvent, useState } from 'react';
import { Input } from './Input';

export type TagMultiInputProps = {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  tagClassName?: string;
  disabled?: boolean;
  validator?: (value: string) => boolean;
  errorMessage?: string;
  label?: string;
  required?: boolean;
  onError?: (message: string) => void;
  separator?: string;
  tagColor?: 'indigo' | 'viridian' | 'salmon' | 'gold' | 'neutral' | 'primary';
};

const tagColorClasses = {
  indigo: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
  viridian: 'bg-viridian-100 text-viridian-800 hover:bg-viridian-200',
  salmon: 'bg-salmon-100 text-salmon-800 hover:bg-salmon-200',
  gold: 'bg-gold-100 text-gold-800 hover:bg-gold-200',
  neutral: 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200',
  primary: 'bg-primary-100 text-primary-800 hover:bg-primary-200',
};

export const TagMultiInput = ({
  values,
  onChange,
  placeholder = 'Add items...',
  className = '',
  inputClassName = '',
  tagClassName = '',
  disabled = false,
  validator,
  errorMessage,
  label,
  required = false,
  onError,
  separator = ',',
  tagColor = 'indigo',
}: TagMultiInputProps) => {
  const [inputValue, setInputValue] = useState('');

  const removeValue = (valueToRemove: string) => {
    onChange(values.filter((value) => value !== valueToRemove));
  };

  const addValue = (newValue: string) => {
    const trimmedValue = newValue.trim();
    if (!trimmedValue) return;

    // Check if value already exists
    if (values.includes(trimmedValue)) {
      onError?.('This value already exists');
      return;
    }

    // Run validator if provided
    if (validator && !validator(trimmedValue)) {
      onError?.(errorMessage || 'Invalid value');
      return;
    }

    onChange([...values, trimmedValue]);
  };

  const addMultipleValues = (inputText: string) => {
    const valueList = inputText
      .split(separator)
      .map((value) => value.trim())
      .filter((value) => value);

    const newValues = valueList.filter((value) => !values.includes(value));

    if (newValues.length > 0) {
      onChange([...values, ...newValues]);
    } else {
      onError?.('Please enter valid values');
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === separator) {
      e.preventDefault();
      addValue(inputValue);
      setInputValue('');
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    addMultipleValues(pastedText);
    setInputValue('');
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (inputValue.trim()) {
      addValue(inputValue);
      setInputValue('');
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}

      <div className="border border-neutral-200 rounded-lg p-3 min-h-[48px] bg-neutral-0">
        <div className="flex flex-wrap gap-2">
          {values.map((value, index) => (
            <div
              key={`${value}-${index}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${tagColorClasses[tagColor]} ${tagClassName}`}
            >
              <span>{value}</span>
              <button
                onClick={() => removeValue(value)}
                className="rounded-full p-0.5 transition-colors"
                disabled={disabled}
                type="button"
                aria-label={`Remove ${value}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <Input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={placeholder}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            onBlur={handleBlur}
            className={`flex-1 min-w-[200px] border-0 focus:ring-0 focus:outline-none ${inputClassName}`}
            disabled={disabled}
            size="small"
          />
        </div>
      </div>
    </div>
  );
};
